import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Download, Eye, Edit, Trash2, FileText, 
  Calendar, Building, DollarSign, Hash, User, Filter, CheckCircle, XCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/DataTable/DataTable';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import Breadcrumb from '../../components/Common/Breadcrumb';
import { toast } from 'react-toastify';
import taxInvoiceService from '../../services/taxInvoiceService';

const TaxInvoice = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelItem, setCancelItem] = useState(null);
  const [stats, setStats] = useState({
    totalInvoices: 0,
    draftInvoices: 0,
    approvedInvoices: 0,
    cancelledInvoices: 0,
    totalValue: 0,
    totalTaxAmount: 0,
    totalAmountWithTax: 0
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0
  });

  // Load data on component mount
  useEffect(() => {
    loadData();
    loadStats();
  }, []);

  // Dummy data for Tax Invoice (fallback)
  const dummyInvoices = [
    {
      id: '1',
      serviceInvoiceNumber: 'TI-2024-001',
      serviceInvoiceDate: '2024-01-15',
      PONo: 'PO-2024-001',
      customerName: 'TechCorp Solutions',
      customerCode: 'CUST-001',
      currency: 'USD',
      totalAmountWithTax: 15000.00,
      totalTaxAmount: 1500.00,
      status: 'Draft',
      paymentStatus: 'Unpaid',
      dueDate: '2024-02-15',
      createdBy: { firstName: 'John', lastName: 'Smith' },
      createdAt: '2024-01-15T10:30:00Z'
    },
    {
      id: '2',
      serviceInvoiceNumber: 'TI-2024-002',
      serviceInvoiceDate: '2024-01-16',
      PONo: 'PO-2024-002',
      customerName: 'Global Manufacturing Inc',
      customerCode: 'CUST-002',
      currency: 'USD',
      totalAmountWithTax: 25000.00,
      totalTaxAmount: 2500.00,
      status: 'Generated',
      paymentStatus: 'Paid',
      dueDate: '2024-02-16',
      createdBy: { firstName: 'Maria', lastName: 'Garcia' },
      createdAt: '2024-01-16T14:20:00Z'
    }
  ];

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await taxInvoiceService.getAllTaxInvoices({
        page: pagination.current,
        limit: 10,
        search: searchTerm,
        status: 'Draft' // Show only Draft invoices for user actions (approve/cancel/delete)
      });

      if (response.success) {
        // Transform _id to id for consistency
        const transformedData = (response.data || []).map(invoice => ({
          ...invoice,
          id: invoice._id || invoice.id
        }));
        setData(transformedData);
        setPagination(response.pagination);
      } else {
        toast.error('Failed to load tax invoices');
        // Use dummy data as fallback
        setData(dummyInvoices);
      }
    } catch (error) {
      console.error('Error loading tax invoices:', error);
      toast.error('Failed to load tax invoices');
      // Use dummy data as fallback
      setData(dummyInvoices);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await taxInvoiceService.getTaxInvoiceStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Search functionality
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    loadData();
  };

  // Pagination
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
    loadData();
  };

  // Filter data based on search term (for client-side filtering if needed)
  const filteredData = data.filter(item =>
    item.serviceInvoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.PONo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.customerCode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Table columns configuration
  const columns = [
    {
      key: 'serviceInvoiceNumber',
      label: 'Invoice #',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-blue-600" />
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      )
    },
    {
      key: 'serviceInvoiceDate',
      label: 'Invoice Date',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-gray-500" />
          <span>{new Date(value).toLocaleDateString()}</span>
        </div>
      )
    },
    {
      key: 'PONo',
      label: 'PO No.',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <Hash size={16} className="text-gray-500" />
          <span className="font-medium">{value || '-'}</span>
        </div>
      )
    },
    {
      key: 'customerName',
      label: 'Customer Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{row.customerCode}</div>
        </div>
      )
    },
    {
      key: 'currency',
      label: 'Currency',
      sortable: true,
      render: (value) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {value}
        </span>
      )
    },
    {
      key: 'totalAmountWithTax',
      label: 'Total Amount',
      sortable: true,
      render: (value, row) => (
        <div className="text-right">
          <div className="font-medium text-gray-900">
            {row.currency} {value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </div>
          <div className="text-sm text-gray-500">
            Tax: {row.currency} {row.totalTaxAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusConfig = {
          'Draft': { color: 'bg-gray-100 text-gray-800', icon: '📝' },
          'Generated': { color: 'bg-blue-100 text-blue-800', icon: '📄' },
          'Report Generated': { color: 'bg-green-100 text-green-800', icon: '✅' },
          'Sent': { color: 'bg-purple-100 text-purple-800', icon: '📤' },
          'Paid': { color: 'bg-green-100 text-green-800', icon: '💰' },
          'Cancelled': { color: 'bg-red-100 text-red-800', icon: '❌' }
        };
        const config = statusConfig[value] || statusConfig['Draft'];
        
        return (
          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
            <span>{config.icon}</span>
            {value}
          </span>
        );
      }
    },
    {
      key: 'paymentStatus',
      label: 'Payment',
      sortable: true,
      render: (value) => {
        const paymentConfig = {
          'Unpaid': { color: 'bg-red-100 text-red-800', icon: '💸' },
          'Partially Paid': { color: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
          'Paid': { color: 'bg-green-100 text-green-800', icon: '✅' },
          'Overdue': { color: 'bg-red-100 text-red-800', icon: '⚠️' }
        };
        const config = paymentConfig[value] || paymentConfig['Unpaid'];
        
        return (
          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
            <span>{config.icon}</span>
            {value}
          </span>
        );
      }
    }
  ];

  // Event handlers
  const handleAddNew = () => {
    navigate('/sales/tax-invoice/new');
  };

  const handleView = (item) => {
    navigate(`/sales/tax-invoice/view/${item.id}`);
  };

  const handleEdit = (item) => {
    navigate(`/sales/tax-invoice/edit/${item.id}`);
  };

  const handleApprove = (item) => {
    // Navigate to approve page (read-only edit mode)
    navigate(`/sales/tax-invoice/approve/${item.id}`);
  };

  const handleCancel = (item) => {
    setCancelItem(item);
    setShowCancelModal(true);
  };

  const confirmCancel = async () => {
    if (!cancelItem) return;

    try {
      setLoading(true);
      const response = await taxInvoiceService.updateTaxInvoice(cancelItem.id, {
        status: 'Cancelled'
      });

      if (response.success) {
        toast.success('Tax invoice cancelled successfully');
        setShowCancelModal(false);
        setCancelItem(null);
        loadData(); // Reload the list
        loadStats(); // Reload statistics
      } else {
        toast.error(response.message || 'Failed to cancel tax invoice');
      }
    } catch (error) {
      console.error('Error cancelling tax invoice:', error);
      toast.error('Failed to cancel tax invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (item) => {
    setDeleteItem(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;

    try {
      setLoading(true);
      // Use _id if available, otherwise fall back to id
      const invoiceId = deleteItem._id || deleteItem.id;
      if (!invoiceId) {
        toast.error('Invalid invoice ID');
        return;
      }
      
      console.log('Deleting invoice with ID:', invoiceId);
      // Call the API to delete the invoice
      const response = await taxInvoiceService.deleteTaxInvoice(invoiceId);
      
      console.log('Delete response:', response);
      
      if (response.success) {
        toast.success('Tax invoice deleted successfully');
        setShowDeleteModal(false);
        setDeleteItem(null);
        // Reload data and stats after successful deletion
        loadData();
        loadStats();
      } else {
        toast.error(response.message || 'Failed to delete tax invoice');
      }
    } catch (error) {
      console.error('Error deleting tax invoice:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete tax invoice';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteItem(null);
  };

  const handleExport = () => {
    // Export functionality
    console.log('Exporting tax invoices...');
    toast.success('Tax invoices exported successfully');
  };

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Sales',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Transactions',
      path: '/sales?tab=transactions',
      onClick: () => navigate('/sales?tab=transactions')
    },
    {
      label: 'Tax Invoice',
      path: '/sales/tax-invoice',
      isCurrent: true
    }
  ];

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Tax Invoice</h1>
          <p>Manage tax invoices and billing</p>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={handleAddNew}>
            <Plus size={18} />
            Generate Tax Invoice
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <FileText size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalInvoices}</div>
          <div className="metric-title">Total Invoices</div>
          <div className="metric-subtitle">All tax invoices</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Calendar size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Draft</span>
            </div>
          </div>
          <div className="metric-value">{stats.draftInvoices}</div>
          <div className="metric-title">Draft Invoices</div>
          <div className="metric-subtitle">In preparation</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Approved</span>
            </div>
          </div>
          <div className="metric-value">{stats.approvedInvoices || 0}</div>
          <div className="metric-title">Approved Invoices</div>
          <div className="metric-subtitle">Ready for sending</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <XCircle size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Cancelled</span>
            </div>
          </div>
          <div className="metric-value">{stats.cancelledInvoices || 0}</div>
          <div className="metric-title">Cancelled Invoices</div>
          <div className="metric-subtitle">Cancelled invoices</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Building size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">${stats.totalAmountWithTax.toLocaleString('en-US', { minimumFractionDigits: 0 })}</div>
          <div className="metric-title">Total Amount</div>
          <div className="metric-subtitle">All invoices combined</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Subtotal</span>
            </div>
          </div>
          <div className="metric-value">${(stats.totalValue || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}</div>
          <div className="metric-title">Invoice Amount</div>
          <div className="metric-subtitle">Total without tax</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Hash size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Tax</span>
            </div>
          </div>
          <div className="metric-value">${(stats.totalTaxAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}</div>
          <div className="metric-title">Total Tax</div>
          <div className="metric-subtitle">Total tax amount</div>
        </div>
      </div>


      {/* Data Table */}
      <DataTable
        data={filteredData}
        columns={columns}
        loading={loading}
        onView={handleView}
        onEdit={handleEdit}
        onApprove={(row) => {
          // Only show approve action for Draft invoices
          if (row.status === 'Draft') {
            handleApprove(row);
          }
        }}
        onCancel={(row) => {
          // Only show cancel action for Draft invoices
          if (row.status === 'Draft') {
            handleCancel(row);
          }
        }}
        onDelete={handleDelete}
        onExport={handleExport}
        noCard={true}
        className="tax-invoice-table"
        pageSize={10}
      />

      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteItem(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Tax Invoice"
        message={`Are you sure you want to delete invoice ${deleteItem?.serviceInvoiceNumber}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <ConfirmationModal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setCancelItem(null);
        }}
        onConfirm={confirmCancel}
        title="Cancel Tax Invoice"
        message={`Are you sure you want to cancel invoice ${cancelItem?.serviceInvoiceNumber}? This action cannot be undone.`}
        confirmText="Cancel Invoice"
        cancelText="No, Keep It"
        type="warning"
      />
    </div>
  );
};

export default TaxInvoice;
